import { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useGardenSettings } from './GardenSettingsContext';
import { plantsApi } from '../services/api';
import { determineDashboardState } from '../utils/carePhaseUtils';

const CANVAS_KEY = 'gartenplaner_canvas';

/**
 * DashboardContext — manages top-level view switching and dashboard state.
 *
 * Shape:
 * {
 *   activeView: 'editor' | 'dashboard',
 *   setActiveView: fn,
 *   dashboardTab: 'uebersicht' | 'pflegekalender',
 *   setDashboardTab: fn,
 *   placedPlants: [],                 // Plant objects currently on canvas
 *   setPlacedPlants: fn,              // Called by GardenCanvas on load/update
 *   allPlants: [],                    // Full plant catalog — set by Chunk E
 *   setAllPlants: fn,
 *   carePhase: {                      // Derived dashboard state (useMemo over
 *     state: '0' | 'A' | 'B',         //   isConfigured / placedPlants / allPlants)
 *     activePlants: [],
 *     nextRelevantMonth: null
 *   },
 *   modalOpen: boolean,
 *   setModalOpen: fn,
 * }
 *
 * State determination (Chunk A — simple version, full computation in Chunk D):
 *   '0': !isConfigured OR placedPlants.length === 0
 *   'A': isConfigured AND placedPlants.length > 0 (activePlants from carePhaseUtils = [])
 *   'B': isConfigured AND activePlants.length > 0  (wired up in Chunk D)
 */

export const DashboardContext = createContext(null);

const ACTIVE_VIEW_KEY = 'gartenplaner_active_view';

/**
 * Optimistic fast-paint seed: read placed plant IDs from the localStorage canvas
 * cache so the Dashboard renders instantly without waiting for the network. This
 * is a CACHE only — GardenStartupLoader (#484) overwrites placedPlants with the
 * server-authoritative state on startup, and MainLayout keeps it in sync once the
 * editor opens.
 */
const readCachedPlacedPlants = () => {
  try {
    const saved = localStorage.getItem(CANVAS_KEY);
    if (!saved) return [];
    const json = JSON.parse(saved);
    if (!json.objects || json.objects.length === 0) return [];
    return json.objects
      .filter((obj) => obj.objectType === 'plant' && obj.plantId)
      .map((obj) => ({ plantId: obj.plantId, id: obj.plantId }));
  } catch {
    // Corrupted data — ignore, Dashboard will show State 0
    return [];
  }
};

export const DashboardProvider = ({ children }) => {
  const { isConfigured } = useGardenSettings();

  // Top-level view: persisted in sessionStorage so navigating away (e.g. to /login)
  // and returning via browser back restores the previous view instead of always
  // landing on the dashboard.
  const [activeView, setActiveViewRaw] = useState(
    () => sessionStorage.getItem(ACTIVE_VIEW_KEY) ?? 'dashboard'
  );

  const setActiveView = (view) => {
    sessionStorage.setItem(ACTIVE_VIEW_KEY, view);
    setActiveViewRaw(view);
  };
  const [dashboardTab, setDashboardTab] = useState('uebersicht');
  const [mobileTab, setMobileTab] = useState('uebersicht');

  // Desktop-only: the settings/account panel is opened as an overlay from the
  // header gear. Mobile reaches the same SettingsView via the "mehr" bottom tab,
  // so this flag is unused there. (#529 — DSGVO actions must be desktop-reachable.)
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Plants currently placed on the canvas — set by GardenCanvas via context.
  // Seeded from the localStorage cache for an instant first paint (see above).
  const [placedPlants, setPlacedPlants] = useState(readCachedPlacedPlants);

  // Full plant catalog — set by PlantCatalog / GardenCanvas in Chunk E
  const [allPlants, setAllPlants] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);

  // Load full plant catalog from backend on mount so determineDashboardState()
  // can match placed plant IDs against care_instructions data.
  useEffect(() => {
    let cancelled = false;

    plantsApi
      .getFullCatalog()
      .then((plants) => {
        if (!cancelled && Array.isArray(plants)) {
          setAllPlants(plants);
        }
      })
      .catch(() => {
        // Backend not available — allPlants stays [], State B won't trigger
      });

    return () => { cancelled = true; };
  }, []);

  // Care phase is derived state — compute it during render instead of mirroring
  // it into a useState via an effect (avoids a setState-in-effect render cascade).
  const carePhase = useMemo(() => {
    if (!isConfigured || placedPlants.length === 0) {
      return { state: '0', activePlants: [], nextRelevantMonth: null };
    }
    // Extract plant IDs — placedPlants may be full objects or bare IDs
    const ids = placedPlants
      .map((p) => (typeof p === 'object' && p !== null ? (p.id ?? p.plantId ?? null) : p))
      .filter(Boolean);
    const month = new Date().getMonth() + 1;
    return determineDashboardState(ids, allPlants, month);
  }, [isConfigured, placedPlants, allPlants]);

  const value = {
    activeView,
    setActiveView,
    dashboardTab,
    setDashboardTab,
    mobileTab,
    setMobileTab,
    settingsOpen,
    setSettingsOpen,
    placedPlants,
    setPlacedPlants,
    allPlants,
    setAllPlants,
    carePhase,
    modalOpen,
    setModalOpen,
  };

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  );
};

export const useDashboard = () => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error('useDashboard must be used within a DashboardProvider');
  }
  return context;
};
